import { BUSINESS, TRUST_BADGES, MEDIA } from "@/data";
import { CallButton, DirectionsButton } from "./Buttons";
import * as Icons from "lucide-react";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-12 md:pt-16 pb-16 md:pb-24 overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white"
    >
      {/* Soft brand orbs */}
      <div className="absolute -top-20 -left-32 w-96 h-96 rounded-full bg-green-100/60 blur-[100px] z-0 pointer-events-none" aria-hidden />
      <div className="absolute top-32 -right-32 w-96 h-96 rounded-full bg-orange-100/60 blur-[100px] z-0 pointer-events-none" aria-hidden />
      <div className="absolute inset-0 grid-bg-light opacity-50 z-0 pointer-events-none" aria-hidden />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — copy */}
          <div>
            {/* Tagline strip */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-red/10 border border-brand-red/20 mb-6 animate-fade-up">
              <Icons.Sparkles className="w-3.5 h-3.5 text-brand-red" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-red">
                {BUSINESS.tagline}
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display font-black tracking-tight text-brand-navy text-[40px] sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] animate-fade-up"
              style={{ animationDelay: "0.05s" }}
            >
              Miami Gardens'<br />
              <span className="text-brand-red">Trusted Phone Plug</span><br />
              <span className="text-slate-700">For Repairs &amp; Devices.</span>
            </h1>

            <p
              className="mt-6 md:mt-7 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.15s" }}
              data-testid="hero-sub"
            >
              Fast iPhone &amp; Samsung repairs, unlocked phones, accessories, prepaid service, and honest tech help — all in one local store.
            </p>

            <div
              className="mt-7 flex flex-wrap items-center gap-3 animate-fade-up"
              style={{ animationDelay: "0.25s" }}
            >
              <CallButton testid="hero-call-btn">Call Now · {BUSINESS.phone}</CallButton>
              <DirectionsButton testid="hero-directions-btn" variant="secondary" />
              <a
                href="#pricing"
                data-testid="hero-pricing-link"
                className="inline-flex items-center gap-2 font-bold font-display rounded-full transition-all active:scale-95 py-3.5 px-6 text-base text-brand-navy hover:text-brand-red"
              >
                View Repair Prices
                <Icons.ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Address strip */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Icons.MapPin className="w-4 h-4 text-brand-red" />
                <span className="font-medium">{BUSINESS.address}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icons.Clock className="w-4 h-4 text-brand-blue" />
                <span>Open today · call to confirm hours</span>
              </div>
            </div>
          </div>

          {/* Right — hero image card */}
          <div className="relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-100 to-white border border-slate-200 shadow-xl">
              <div className="aspect-[4/3] relative">
                <img
                  src={MEDIA.plugmanHero}
                  alt="Plug-Man repairing a phone at the Your Phone Plug counter"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/30 via-transparent to-transparent" />
              </div>
              {/* Floating badge */}
              <div className="absolute top-4 left-4 bg-white rounded-full px-4 py-2 shadow-md flex items-center gap-2 animate-float">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-bold text-brand-navy">15-min repairs · open now</span>
              </div>
            </div>

            {/* Floating warranty badge */}
            <div className="absolute -bottom-6 -left-4 md:-left-8 bg-brand-red text-white rounded-2xl p-4 shadow-xl glow-red rotate-[-3deg] hidden sm:block">
              <div className="font-display font-black text-2xl leading-none">90</div>
              <div className="text-[10px] uppercase tracking-wider font-bold">DAY WARRANTY</div>
            </div>
            {/* Floating diag badge */}
            <div className="absolute -top-4 -right-2 md:-right-6 bg-brand-blue text-white rounded-2xl p-4 shadow-xl glow-blue rotate-[3deg] hidden sm:block">
              <div className="font-display font-black text-2xl leading-none">$0</div>
              <div className="text-[10px] uppercase tracking-wider font-bold">FREE DIAG</div>
            </div>
          </div>
        </div>

        {/* Trust badges strip */}
        <div
          className="mt-16 md:mt-20 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 animate-fade-up"
          style={{ animationDelay: "0.4s" }}
          data-testid="trust-badges"
        >
          {TRUST_BADGES.map((b, i) => {
            const Icon = Icons[b.icon] || Icons.CheckCircle2;
            return (
              <div
                key={b.label}
                data-testid={`trust-badge-${i}`}
                className="card-soft card-soft-hover px-4 py-3.5 flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-xl bg-green-50 grid place-items-center shrink-0">
                  <Icon className="w-4.5 h-4.5 text-brand-red" />
                </div>
                <span className="text-sm font-bold text-brand-navy leading-tight">{b.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
